import { useCallback, useEffect, useState } from 'react'
import api from '../utils/api'
import { TimeWindow, TimeWindowFilter } from '@/components/TimeWindowFilter'
import { Button } from '@/components/Button'
import { SetupRequiredState } from '@/components/SetupRequiredState'
import { useToast } from '@/components/Toast'
import { apiErrorMessage, parseApiError, type ParsedApiError } from '@/utils/apiErrors'

interface Resource {
  id: string
  resource_type: string
  name: string
  status: string
  aws_region: string
  resource_id?: string
  created_at?: string
  error_message?: string
}

export default function BeaconResources() {
  const { toast } = useToast()
  const [window, setWindow] = useState<TimeWindow>('week')
  const [rows, setRows] = useState<Resource[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [setupError, setSetupError] = useState<ParsedApiError | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/api/v1/beacon/resources', { params: { window } })
      setRows(res.data.resources || [])
      setSetupError(null)
    } catch (err: unknown) {
      const parsed = parseApiError(err)
      if (parsed.isSetupIncomplete || parsed.status === 409 || parsed.status === 422) {
        setSetupError(parsed)
      } else {
        toast.error('Failed to load ledger', parsed.message)
      }
    } finally {
      setLoading(false)
    }
  }, [window, toast])

  useEffect(() => { load() }, [load])

  const requestDelete = async (id: string) => {
    setBusy(id)
    try {
      const res = await api.delete(`/api/v1/beacon/resources/${id}`, { data: { dry_run: false } })
      if (res.data.status === 'awaiting_approval') {
        toast.info('HITL required', 'Delete queued for approval — open Approvals.')
      } else {
        toast.success('Delete', res.data.status)
      }
      await load()
    } catch (err: unknown) {
      const parsed = parseApiError(err)
      if (parsed.isSetupIncomplete) {
        setSetupError(parsed)
      }
      toast.error('Delete failed', apiErrorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-slate-900">Resources & artifacts</h2>
          <p className="text-sm text-slate-600">Everything Beacon created, with status and delete-by-ledger.</p>
        </div>
        <TimeWindowFilter value={window} onChange={setWindow} />
      </div>
      {setupError && (
        <SetupRequiredState message={setupError.message} cta={setupError.cta} />
      )}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Region</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No resources in this window.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-mono text-xs">{r.resource_type}</td>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-800">{r.name}</div>
                  <div className="text-xs text-slate-400">{r.resource_id || r.id}</div>
                </td>
                <td className="px-4 py-3">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{r.status}</span>
                </td>
                <td className="px-4 py-3 text-slate-600">{r.aws_region}</td>
                <td className="px-4 py-3 text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</td>
                <td className="px-4 py-3 text-right">
                  {r.status === 'active' && (
                    <Button
                      variant="secondary"
                      disabled={busy === r.id}
                      onClick={() => requestDelete(r.id)}
                    >
                      Delete
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
